import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    model,
    ViewChild,
    ViewEncapsulation,
    output,
} from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { IMediaBankFileReference } from '../media-bank';
import { FiTextPanelHelperService } from '../fi-text-panel/text-panel-services/fi-text-panel-helper.service';
import { TextEditorHistoryService } from '../fi-text-editor/fi-text-editor-helper-services/text-editor-history.service';
import { eventFindInPath, findClosest, isDefined } from '../utils';
import { Animations } from '../utils/animations';
import { ObjectTypes } from '../types';
import { ISimpleEditorAction } from './fi-simple-contenteditable.d';
import { FiContenteditableComponent } from '../fi-contenteditable/fi-contenteditable.component';
import { ITextEditorLinkAttachment } from '../fi-text-editor/fi-text-editor';
import { AdvancedTextPanelMediaLinkService } from '../fi-text-panel/text-panel-services/advanced-text-panel-media-link.service';
import { TextEditorInsertLinkService } from '../fi-text-editor/fi-text-editor-insert-link/text-editor-insert-link.service';
import { ILightWeightUser } from '@fi-shared/models';
import { TextEditorInsertEmbedVideoService } from '@fi-shared/fi-text-editor/fi-text-editor-insert-embed-video/text-editor-insert-embed-video.service';
import { FiAttachmentsListComponent } from '@fi-shared/media-bank/fi-attachments/fi-attachments-list.component';
import { FiTextItemMediaPanelHandlerComponent } from '@fi-app/utilities/insert-media/fi-text-item-media-panel-handler/fi-text-item-media-panel-handler.component';
import { AdvancedTextHandlerService } from '@fi-shared/fi-text-editor/fi-text-editor-helper-services/advanced-text-handler.service';

@Component({
    selector: 'fi-simple-contenteditable',
    templateUrl: './fi-simple-contenteditable.component.html',
    styleUrls: ['./fi-simple-contenteditable.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-simple-contenteditable' },
    animations: [Animations.fade, Animations.fadeHeight()],
    standalone: false,
})
export class FiSimpleContenteditableComponent implements AfterViewInit {
    @Input({ required: true }) objectType: ObjectTypes;
    @Input({ required: true }) content: string = '';
    public contentChange = output<string>();

    @Input() contentInputRequired: boolean;
    @Input() contentInputLabel: string;
    @Input() contentInputHint: string;
    @Input() contentInputPlaceholder: string;
    @Input() editorActions: ISimpleEditorAction[] = [];
    @Input() isAttachmentDividerShown: boolean = true;

    @Input() isTagUserFeatureEnabled: boolean = false;
    @Input() taggedUsers: ILightWeightUser[];
    @Input() isEmbedVideoFeatureEnabled: boolean = false;
    @Input() isGifFeatureEnabled: boolean = false;
    @Input() isInsertMediaEnabled: boolean = true;
    @Input() isInsertLinkEnabled: boolean = true;
    @Input() preventInsertingMediaIntoTextEditor: boolean = true;
    @Input() isInternalLinksAllowed: boolean = true;
    @Input() preventEditYoutubeLinks: boolean = false;
    @Input() isMediaSettingsAllowed: boolean = true;

    @Input() isDisabled: boolean = false;

    @Input() actionInsideEditor: ISimpleEditorAction;
    public mediaBankFileReferences = model<IMediaBankFileReference[]>([]);

    @Input() linkAttachments: ITextEditorLinkAttachment[] = [];
    public linkAttachmentsChange = output<ITextEditorLinkAttachment[]>();

    private handleClickInDialogFn: (target: HTMLElement) => boolean;
    // Optional input to set an additional panel visibility handler.
    @Input('currentDialogClass') set currentDialogClass(value: string) {
        if (isDefined(value)) {
            this.handleClickInDialogFn = (target: HTMLElement) => !isDefined(findClosest(target, `.${value}`));
        }
    }

    @ViewChild(FiContenteditableComponent, { static: true }) contenteditableComponent: FiContenteditableComponent;
    @ViewChild(FiAttachmentsListComponent, { static: false }) attachmentsListComponent: FiAttachmentsListComponent;
    @ViewChild(FiTextItemMediaPanelHandlerComponent, { static: false })
    mediaPanelHandlerComponent: FiTextItemMediaPanelHandlerComponent;

    private _isTextPanelVisible$ = new BehaviorSubject(false);
    protected get isTextPanelVisible$(): Observable<boolean> {
        return this._isTextPanelVisible$.asObservable();
    }
    protected isDefined = isDefined;

    constructor(
        private fiTextPanelHelperService: FiTextPanelHelperService,
        private textEditorHistoryService: TextEditorHistoryService,
        private cdr: ChangeDetectorRef,
        private advancedTextPanelMediaLinkService: AdvancedTextPanelMediaLinkService,
        private textEditorInsertEmbedVideoService: TextEditorInsertEmbedVideoService,
        private textEditorInsertLinkService: TextEditorInsertLinkService,
        private advancedTextHandlerService: AdvancedTextHandlerService,
    ) {}

    public ngAfterViewInit(): void {
        this.subscribeToTextPanelVisibilityChangeOnClick();
    }

    private _isFirstClick = true;

    private subscribeToTextPanelVisibilityChangeOnClick() {
        this.fiTextPanelHelperService.subscribeToTextPanelVisibilityChangeOnClick(
            () => this.contenteditableComponent.elementRef.nativeElement,
            this.ngOnDestroy$,
            (isPanelVisible: boolean) => {
                if (this._isFirstClick) {
                    this._isFirstClick = false;
                    return;
                }
                this._isTextPanelVisible$.next(isPanelVisible && !this.isDisabled);
            },
            this.handleClickInDialogFn,
        );
    }

    protected handleContentEditableClick($event: MouseEvent) {
        this.handleLinkClick($event);
        this.advancedTextHandlerService.onClick$.next($event);
    }

    private handleLinkClick($event: MouseEvent) {
        const link = eventFindInPath($event, 'ccm-text-item-link') || eventFindInPath($event, 'a[ccm-text-item-link]');
        if (!link) {
            return;
        }

        $event.preventDefault();
        const ccmTextItemLink = JSON.parse(link.getAttribute('text-item-model'));
        if (!isDefined(ccmTextItemLink)) {
            return;
        }
        const range = new Range();
        range.selectNode(link.firstChild);
        this.advancedTextPanelMediaLinkService.setTextEditorActiveButtons({ link: true });
        this.textEditorInsertLinkService
            .insertLink({
                parentElement: link.parentElement,
                linkToType: ccmTextItemLink.Type,
                range: range,
            })
            .afterClosed(() => this.advancedTextPanelMediaLinkService.setTextEditorActiveButtons({ link: false }));
    }

    protected onContentChange($event: string): void {
        this.content = $event;
        this.contentChange.emit(this.content);
    }

    protected addMediaBankFileReferences(mediaBankFileReferences: IMediaBankFileReference[]): void {
        const mbRefs = this.mediaBankFileReferences();
        if (!isDefined(mbRefs)) {
            this.mediaBankFileReferences.set(mediaBankFileReferences);
        } else {
            this.mediaBankFileReferences.update((mbRefs) => [...mbRefs, ...mediaBankFileReferences]);
        }
    }

    protected onInsertLinkAttachment(linkAttachment: ITextEditorLinkAttachment) {
        if (!isDefined(this.linkAttachments)) {
            this.linkAttachments = [];
        }
        this.linkAttachments = this.linkAttachments.concat(linkAttachment);
        this.linkAttachmentsChange.emit(this.linkAttachments);
    }

    protected onLinkAttachmentsChange(linkAttachments: ITextEditorLinkAttachment[]) {
        this.linkAttachments = linkAttachments;
        this.linkAttachmentsChange.emit(linkAttachments);
    }

    protected onFocus(contentEditableElement: HTMLElement) {
        if (!isDefined(contentEditableElement)) {
            return;
        }

        if (!contentEditableElement.hasAttribute('id')) {
            contentEditableElement.setAttribute('id', this.textEditorHistoryService.getContentEditableElementUid());
        }
        this.textEditorHistoryService.setElement(contentEditableElement);

        this._isTextPanelVisible$.next(true);
        this.advancedTextHandlerService.setCurrent(contentEditableElement);
        this.cdr.markForCheck();
    }

    protected isAttachmentsSectionShown(): boolean {
        return !!this.mediaBankFileReferences()?.length || !!this.linkAttachments?.length;
    }

    protected onNewUserTagged(newTaggedUser: ILightWeightUser) {
        if (this.taggedUsers && !this.taggedUsers.find((u) => u.Id === newTaggedUser.Id)) {
            // add new tagged user to array for proper avatar updating after switching from edit to view mode
            this.taggedUsers.push(newTaggedUser);
        }
    }

    protected onEditLinkAttachment(attachment: ITextEditorLinkAttachment) {
        this.textEditorInsertEmbedVideoService.openInsertEmbedVideoDialog(attachment, {
            existingLinkAttachments: this.linkAttachments,
            onYesButtonClick: () => {
                this.cdr.markForCheck();
                this.attachmentsListComponent.emitDetectChanges(); // for proper attachments list update
            },
        });
    }

    private ngOnDestroy$: Subject<void> = new Subject<void>();
    public ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
